import { apiRoutes } from '@/constants/api-routes';
import { apiRequest, type QueryParams } from './http';
import type {
  ArticleLevel,
  ArticleStatus,
  ArticleTone,
  ArticleTopic,
  ArticleView,
  PaginatedArticles,
} from '@/types/api';
import type { ListArticlesFilters } from './articles.service';

/**
 * Admin-only article API: list-with-drafts, by-id fetch, create, patch,
 * delete. Mirrors server endpoints on /articles (mutations) and
 * /admin/articles (reads). The admin editor and admin articles dashboard
 * are the only callers.
 */

export interface CreateArticlePayload {
  title: string;
  topic: ArticleTopic;
  level: ArticleLevel;
  slug?: string;
  summary?: string;
  body?: string;
  tone?: ArticleTone;
  mins?: number;
  author?: string | null;
  status?: ArticleStatus;
  isFeatured?: boolean;
}

export interface UpdateArticlePayload {
  title?: string;
  slug?: string;
  summary?: string;
  body?: string;
  topic?: ArticleTopic;
  level?: ArticleLevel;
  tone?: ArticleTone;
  mins?: number;
  author?: string | null;
  status?: ArticleStatus;
  isFeatured?: boolean;
}

function toQueryParams(filters: ListArticlesFilters): QueryParams {
  const query: QueryParams = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    query[key] = value as QueryParams[string];
  }
  return query;
}

export const adminArticlesService = {
  async list(filters: ListArticlesFilters = {}): Promise<PaginatedArticles> {
    return apiRequest<PaginatedArticles>(apiRoutes.adminArticles.list, {
      method: 'GET',
      query: toQueryParams(filters),
    });
  },

  async getById(id: string): Promise<ArticleView> {
    return apiRequest<ArticleView>(apiRoutes.adminArticles.one(id), { method: 'GET' });
  },

  async create(payload: CreateArticlePayload): Promise<ArticleView> {
    return apiRequest<ArticleView>(apiRoutes.articles.create, {
      method: 'POST',
      body: payload,
    });
  },

  async update(id: string, payload: UpdateArticlePayload): Promise<ArticleView> {
    return apiRequest<ArticleView>(apiRoutes.articles.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },

  async remove(id: string): Promise<void> {
    await apiRequest<void>(apiRoutes.articles.one(id), { method: 'DELETE' });
  },

  /**
   * Admin-only. Uploads the per-article thumbnail/header image. Same
   * multipart contract as `booksService.uploadCover` — server reads the
   * first file from the request via `req.file()`.
   */
  async uploadThumbnail(id: string, file: File): Promise<ArticleView> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return apiRequest<ArticleView>(apiRoutes.articles.thumbnail(id), {
      method: 'POST',
      body: formData,
      raw: true, // let fetch set the multipart boundary header for us
    });
  },

  /**
   * Admin-only. Clears `thumbnail_image` and best-effort unlinks the file
   * on disk. Returns the refreshed article with `thumbnailImage: null`.
   */
  async deleteThumbnail(id: string): Promise<ArticleView> {
    return apiRequest<ArticleView>(apiRoutes.articles.thumbnail(id), {
      method: 'DELETE',
    });
  },
};
